import { useCallback, useEffect, useState } from "react";
import { Truck, Plus, GasPump, ClipboardText, Warning, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function ExpiryBadge({ date }) {
  if (!date) return <span className="text-xs text-muted-foreground">—</span>;
  const d = daysUntil(date);
  const urgent = d !== null && d <= 14;
  return (
    <span className="flex flex-col">
      <span className="font-mono text-xs">{date}</span>
      {urgent && d >= 0 && (
        <Badge variant="outline" className="mt-1 w-fit rounded-sm border-red-500/40 bg-red-500/10 text-[10px] font-bold uppercase text-red-400">
          <Warning size={10} className="mr-1" /> {d}d left
        </Badge>
      )}
      {d !== null && d < 0 && (
        <Badge variant="outline" className="mt-1 w-fit rounded-sm border-red-500/40 bg-red-500/10 text-[10px] font-bold uppercase text-red-400">Expired</Badge>
      )}
    </span>
  );
}

export default function VehiclesPanel({ canEdit = true }) {
  const [vehicles, setVehicles] = useState([]);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [form, setForm] = useState({ plate_number: "", insurance_expiry: "", insurance_company: "", puspakom_expiry: "", roadtax_expiry: "", max_fuel_limit: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const openEdit = (v) => {
    setEditing(v);
    setEditForm({
      insurance_company: v.insurance_company || "", insurance_expiry: v.insurance_expiry || "",
      puspakom_expiry: v.puspakom_expiry || "", roadtax_expiry: v.roadtax_expiry || "",
      max_fuel_limit: v.max_fuel_limit || "",
    });
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/vehicles/${editing.id}`, editForm);
      toast.success(`Vehicle ${editing.plate_number} documents updated`);
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const refresh = useCallback(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/fuel").then((r) => setFuelLogs(r.data)).catch(() => {});
    api.get("/inspections").then((r) => setInspections(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vehicles", form);
      toast.success(`Vehicle ${form.plate_number} registered`);
      setForm({ plate_number: "", insurance_expiry: "", insurance_company: "", puspakom_expiry: "", roadtax_expiry: "", max_fuel_limit: "" });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setFuelLimit = async (v) => {
    const val = window.prompt(`Max fuel limit (RM) for ${v.plate_number}:`, v.max_fuel_limit || "");
    if (val === null) return;
    try {
      await api.patch(`/vehicles/${v.id}`, { max_fuel_limit: parseFloat(val) || 0 });
      toast.success("Fuel limit updated");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
    <Tabs defaultValue="vehicles" data-testid="vehicles-panel">
      <TabsList className="mb-6 rounded-sm border border-border bg-card">
        <TabsTrigger data-testid="tab-vehicles" value="vehicles" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Fleet Vehicles</TabsTrigger>
        <TabsTrigger data-testid="tab-fuel-logs" value="fuel" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Fuel Logs</TabsTrigger>
        <TabsTrigger data-testid="tab-inspections" value="inspections" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Daily Inspections</TabsTrigger>
      </TabsList>

      <TabsContent value="vehicles" className="space-y-6">
        {canEdit && (
          <form onSubmit={submit} className="space-y-4 rounded-sm border border-border bg-card p-6" data-testid="vehicle-form">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight"><Truck size={20} className="text-primary" /> Register Vehicle</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Plate Number *</Label>
                <Input data-testid="vehicle-plate" required value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value.toUpperCase() })}
                  className="rounded-sm border-input bg-background font-mono uppercase focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Insurance Company</Label>
                <Input data-testid="vehicle-insurer" value={form.insurance_company} onChange={(e) => setForm({ ...form, insurance_company: e.target.value })}
                  className="rounded-sm border-input bg-background focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Insurance Expiry</Label>
                <Input data-testid="vehicle-insurance-expiry" type="date" value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })}
                  className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Puspakom Expiry</Label>
                <Input data-testid="vehicle-puspakom" type="date" value={form.puspakom_expiry} onChange={(e) => setForm({ ...form, puspakom_expiry: e.target.value })}
                  className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Road Tax Expiry</Label>
                <Input data-testid="vehicle-roadtax" type="date" value={form.roadtax_expiry} onChange={(e) => setForm({ ...form, roadtax_expiry: e.target.value })}
                  className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
                <p className="text-[10px] text-muted-foreground">Auto email alert to management 14 days before expiry.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Max Fuel Limit (RM)</Label>
                <Input data-testid="vehicle-fuel-limit" type="number" min="0" step="0.01" value={form.max_fuel_limit} onChange={(e) => setForm({ ...form, max_fuel_limit: e.target.value })}
                  className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
              </div>
            </div>
            <Button data-testid="vehicle-submit" type="submit" disabled={busy}
              className="rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
              <Plus size={16} className="mr-2" /> {busy ? "Saving..." : "Register Vehicle"}
            </Button>
          </form>
        )}

        <div className="overflow-x-auto rounded-sm border border-border" data-testid="vehicles-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Plate</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Insurance</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Puspakom</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Road Tax</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Fuel Limit</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.map((v) => (
                <TableRow key={v.id} className="border-border hover:bg-muted/50" data-testid={`vehicle-row-${v.plate_number}`}>
                  <TableCell className="font-mono font-bold text-primary">{v.plate_number}</TableCell>
                  <TableCell>
                    <ExpiryBadge date={v.insurance_expiry} />
                    <p className="mt-1 text-[10px] text-muted-foreground">{v.insurance_company}</p>
                  </TableCell>
                  <TableCell><ExpiryBadge date={v.puspakom_expiry} /></TableCell>
                  <TableCell><ExpiryBadge date={v.roadtax_expiry} /></TableCell>
                  <TableCell className="text-right">
                    <button data-testid={`vehicle-fuel-limit-${v.plate_number}`} onClick={() => canEdit && setFuelLimit(v)}
                      className="font-mono text-sm font-bold text-foreground underline-offset-4 hover:text-primary hover:underline" disabled={!canEdit}>
                      {v.max_fuel_limit ? `RM ${v.max_fuel_limit}` : "Set"}
                    </button>
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <Button size="sm" variant="outline" data-testid={`vehicle-edit-${v.plate_number}`} onClick={() => openEdit(v)}
                        className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
                        <PencilSimple size={14} className="mr-1" /> Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!vehicles.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">No vehicles registered.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="fuel">
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="fuel-logs-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Vehicle</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Type</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Odometer</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Gauge</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Cost</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Receipt</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fuelLogs.map((f) => (
                <TableRow key={f.id} className="border-border hover:bg-muted/50">
                  <TableCell className="font-mono text-xs">{f.date}</TableCell>
                  <TableCell className="font-mono font-bold text-primary">{f.vehicle_plate}</TableCell>
                  <TableCell className="text-sm"><GasPump size={14} className="mr-1 inline text-primary" />{f.fuel_type}</TableCell>
                  <TableCell className="font-mono">{f.odometer}</TableCell>
                  <TableCell className="font-mono text-xs">{f.gauge_before} → {f.gauge_after}</TableCell>
                  <TableCell className="text-right font-mono font-bold">RM {Number(f.cost).toFixed(2)}</TableCell>
                  <TableCell>
                    {f.receipt_path ? (
                      <a href={fileUrl(f.receipt_path)} target="_blank" rel="noreferrer" data-testid={`fuel-receipt-${f.id}`}
                        className="text-xs font-bold uppercase tracking-wider text-primary hover:text-amber-400 hover:underline">View</a>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{f.logged_by}</TableCell>
                </TableRow>
              ))}
              {!fuelLogs.length && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No fuel logs yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="inspections">
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="inspections-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Staff</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Vehicle</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inspections.map((i) => (
                <TableRow key={i.id} className="border-border hover:bg-muted/50">
                  <TableCell className="font-mono text-xs">{i.date}</TableCell>
                  <TableCell className="text-sm font-semibold">{i.user_name}</TableCell>
                  <TableCell className="font-mono text-xs">{vehicles.find((v) => v.id === i.vehicle_id)?.plate_number || i.vehicle_id || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm border-emerald-500/40 text-[10px] font-bold uppercase text-emerald-400"><ClipboardText size={10} className="mr-1" />Complete</Badge></TableCell>
                </TableRow>
              ))}
              {!inspections.length && <TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">No inspections submitted yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>

      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="border-border bg-background" data-testid="vehicle-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Edit Vehicle — <span className="font-mono text-primary">{editing.plate_number}</span></DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                ["insurance_company", "Insurance Company", "text"],
                ["insurance_expiry", "Insurance Expiry", "date"],
                ["puspakom_expiry", "Puspakom Expiry", "date"],
                ["roadtax_expiry", "Road Tax Expiry", "date"],
                ["max_fuel_limit", "Max Fuel Limit (RM)", "number"],
              ].map(([key, label, type]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</Label>
                  <Input data-testid={`vehicle-edit-${key}`} type={type} value={editForm[key]}
                    onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                    className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
                </div>
              ))}
            </div>
            <Button data-testid="vehicle-edit-save" onClick={saveEdit}
              className="mt-2 w-full rounded-sm bg-primary py-5 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
              Save Changes
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
